import type { GetSiteConfigApiResponse } from '#src/api-handlers/types.ts'
import type { LenusCookieConsentConfig } from '#src/assets/lib/config/types.ts'
import type { Logger } from '#src/assets/lib/logger.ts'
import type { ConsentRecordInputSchema } from '#src/schemas/consentSchema.ts'

export function createApiClient({
  config,
  getVisitorId,
  log,
}: {
  config: LenusCookieConsentConfig
  getVisitorId: () => string
  log: Logger['log']
}) {
  const fetchSiteConfig = async (): Promise<GetSiteConfigApiResponse> => {
    const url = new URL(
      `${config.apiBaseUrl}/public/v1/configs/${config.siteId}?locale=${config.locale}`,
    ).toString()

    log('Fetching config from:', url)

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(
        `Failed to fetch site config: ${response.status} ${response.statusText}`,
      )
    }

    return response.json() as Promise<GetSiteConfigApiResponse>
  }

  const postConsentRecord = async ({
    consents,
    version,
  }: {
    consents: Record<string, boolean>
    version: string
  }): Promise<void> => {
    const url = new URL(`${config.apiBaseUrl}/public/v1/consents`).toString()

    const body: ConsentRecordInputSchema = {
      siteId: config.siteId,
      visitorId: getVisitorId(),
      userAgent: navigator.userAgent,
      locale: config.locale,
      consents,
      version,
    }

    log('Posting consent:', body)

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        throw new Error(`Failed to post consent: ${response.status}`)
      }

      log('Consent recorded successfully')
    } catch (error) {
      console.error('[Lenus Cookie Consent] Failed to record consent:', error)
    }
  }

  return {
    fetchSiteConfig,
    postConsentRecord,
  }
}
